import { observer } from "mobx-react";
import React, { FC, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";

import Popup from "../../Popup";
import { useAppStore } from "../../../../hooks/useAppStore";
import { formatInstant } from "../../../helpers/dataHelper";
import { ApiKeySettingItem } from "../../../../services/apiService";

interface KeyContentBoxProps {
  apiKey: ApiKeySettingItem;
}

const KeyContentBox: FC<KeyContentBoxProps> = observer(({ apiKey }) => {
  const theme = useTheme();
  const appStore = useAppStore();

  const [showRotateConfirm, setShowRotateConfirm] = useState(false);

  const handleRotateConfirm = async () => {
    setShowRotateConfirm(false);
    await appStore.rotateApiKey(apiKey.id);
  };

  return (
    <>
      <Box
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          p: 2,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 1.5 }}
        >
          <Typography sx={{ fontFamily: "monospace", fontWeight: 600 }}>
            {apiKey.prefix}••••••••
          </Typography>
          <Chip
            label={apiKey.status}
            size="small"
            color={apiKey.status === "active" ? "success" : "default"}
            sx={{
              "& .MuiChip-label": {
                fontSize: "12px",
                textTransform: "uppercase",
              },
            }}
          />
        </Stack>

        <Stack spacing={1}>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              Tenants
            </Typography>
            <Typography variant="body2">
              {apiKey.tenants.join(", ") || "-"}
            </Typography>
          </Stack>

          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              Last used
            </Typography>
            <Typography variant="body2">
              {formatInstant(apiKey.last_used)}
            </Typography>
          </Stack>
        </Stack>

        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1.5 }}>
          <Button
            onClick={() => setShowRotateConfirm(true)}
            disabled={
              appStore.isApiKeySettingLoading || appStore.isRotatingApiKey
            }
            variant="outlined"
            color="error"
            size="small"
          >
            {appStore.isRotatingApiKey ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={16} />
                Rotating...
              </Box>
            ) : (
              "Rotate - regenerate key"
            )}
          </Button>
        </Stack>
      </Box>

      <Popup
        isOpen={showRotateConfirm}
        onClose={() => setShowRotateConfirm(false)}
        title="Rotate API key"
        message="Regenerating creates a new key and immediately deactivates the current one. Any integration still using the old key will stop working right away. This cannot be undone."
        actions={[
          {
            label: "Cancel",
            onClick: () => setShowRotateConfirm(false),
            variant: "secondary",
          },
          {
            label: "Confirm",
            onClick: handleRotateConfirm,
            variant: "danger",
          },
        ]}
      />
    </>
  );
});

export default KeyContentBox;
